<template>
  <div class="current-month-cashflow-view">
    <div class="current-cashflow-view-container">
      <action-list-badge v-if="shouldShowActionsBadge"/>
      <cashflow-type-base-summary-amount :out-of-date="outOfDate" :showOshButtonOnSummaryAmount="true" class="card-wrapper"/>
      <cashflow-type-base-riseup-goal v-if="riseupGoal >= 0" :out-of-date="outOfDate" class="card-wrapper"/>
      <cashflow-type-base-variable-expense :out-of-date="outOfDate" :time-frame="'weekly'" class="card-wrapper"/>
      <cashflow-type-base-economy :out-of-date="outOfDate" class="card-wrapper"/>
      <transition-group name="list">
        <div v-for="subtree in trackingCategories" :key="subtree.categoryId">
          <cashflow-type-base-tracking-category :out-of-date="outOfDate" :subtree=subtree class="card-wrapper" />
        </div>
        <tracking-category-creation-section v-if="!outOfDate" :key="'create-section'"/>
      </transition-group>
      <cashflow-type-base-saving-category :out-of-date="outOfDate" class="card-wrapper" />
      <cashflow-type-base-fixed-expense :out-of-date="outOfDate" class="card-wrapper"/>
      <cashflow-type-total-expenses-so-far :out-of-date="outOfDate" class="card-wrapper"/>
      <separator class="divider"/>
      <cashflow-type-base-income :out-of-date="outOfDate" class="card-wrapper"/>
    </div>
    <div class="current-cashflow-view-container">
      <div class="divider" v-if="this.hasExcluded" />
      <cashflow-type-base-excluded v-if="this.hasExcluded" :out-of-date="outOfDate" class="card-wrapper"/>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import BaseUI from 'base-ui';
import Segment from '../../../../../../../Segment';
import cashflowViewConsts from '../../../../../../../constants/cashflow-view';
import TrackingCategoryCreationSection from './components/cashflow-edit/edit-reccuring/TrackingCategoryCreationSection.vue';
import CashflowTypeBaseEconomy from '../generic-components/cashflow-types/cashflow-types-base/CashflowTypeBaseEconomy';
import CashflowTypeBaseExcluded from '../generic-components/cashflow-types/cashflow-types-base/CashflowTypeBaseExcluded';
import CashflowTypeBaseFixedExpense from '../generic-components/cashflow-types/cashflow-types-base/CashflowTypeBaseFixedExpense';
import CashflowTypeBaseIncome from '../generic-components/cashflow-types/cashflow-types-base/CashflowTypeBaseIncome';
import CashflowTypeBaseTrackingCategory from '../generic-components/cashflow-types/cashflow-types-base/CashflowTypeBaseTrackingCategory';
import CashflowTypeBaseSavingCategory from '../generic-components/cashflow-types/cashflow-types-base/CashflowTypeBaseSavingCategory';
import CashflowTypeBaseVariableExpense from '../generic-components/cashflow-types/cashflow-types-base/CashflowTypeBaseVariableExpense';
import CashflowTypeBaseRiseupGoal from '../generic-components/cashflow-types/cashflow-types-base/CashflowTypeBaseRiseupGoal';
import CashflowTypeBaseSummaryAmount from '../generic-components/cashflow-types/cashflow-types-base/CashflowTypeBaseSummaryAmount';
import CashflowTypeTotalExpensesSoFar from '../generic-components/cashflow-types/cashflow-types-base/CashflowTypeTotalExpensesSoFar';
import ActionListBadge from './components/action-list/ActionListBadge';
import { RECOGNIZED_FIXED_EXPENSE_TREND_PAGES, TREND_COMPONENTS } from '../cashflow-trend/cashflow-trends';

export default {
  name: 'CurrentMonthCashflowView',
  components: {
    TrackingCategoryCreationSection,
    CashflowTypeBaseEconomy,
    CashflowTypeBaseExcluded,
    CashflowTypeBaseFixedExpense,
    CashflowTypeBaseIncome,
    CashflowTypeBaseTrackingCategory,
    CashflowTypeBaseSavingCategory,
    CashflowTypeBaseVariableExpense,
    CashflowTypeBaseRiseupGoal,
    CashflowTypeBaseSummaryAmount,
    CashflowTypeTotalExpensesSoFar,
    Separator: BaseUI.Separator,
    ActionListBadge,
  },
  async created() {
    Segment.trackUserGot('CurrentCashflowViewEntered');
    await this.fetchActiveChallenge();
    await this.fetchActions({ isPositiveCf: this.isPredictedCashflowPositive, allPapaQuestionsAnswered: this.allPapasAnswered });
    if (this.$route.query.trendView) {
      if (RECOGNIZED_FIXED_EXPENSE_TREND_PAGES.includes(this.$route.query.trendView)) {
        this.openTrendView(this.$route.query.trendView);
      }
    }
  },
  props: {
    outOfDate: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...mapState('cashflowView', ['finishLoadingAllBudgets', 'cashflowUITree']),
    ...mapState('session', ['activeMember']),
    ...mapGetters('actions', ['shouldShowActionsBadge']),
    ...mapGetters('cashflowView', ['predictedBalanceAtEndOfMonth', 'hasExcluded', 'riseupGoal']),
    ...mapGetters('mergeInvestigationQuestions', ['allPapasAnswered']),
    isPredictedCashflowPositive() {
      return this.predictedBalanceAtEndOfMonth > 0;
    },
    trackingCategories() {
      return this.cashflowUITree[cashflowViewConsts.cashflowTypes.trackingCategory];
    },
  },
  methods: {
    ...mapActions('challenge', ['fetchActiveChallenge']),
    ...mapActions('actions', ['fetchActions']),
    ...mapActions('modalRootStore', ['openModal', 'closeModal']),
    openTrendView(pageName) {
      // wait until this.finishLoadingAllBudgets is true to invoke openModal
      if (!this.finishLoadingAllBudgets) {
        setTimeout(() => {
          this.openTrendView(pageName);
        }, 100);
        return;
      }
      // eslint-disable-next-line no-unused-vars
      const { trendView, ...query } = this.$route.query;
      this.$router.replace({ path: this.$route.path, query });
      this.openModal({
        component: TREND_COMPONENTS.fixedExpense,
        props: { deepLinkToPage: pageName },
      });
    },
  },
};
</script>

<style lang="scss" scoped>
  @import '../../../../../../../../node_modules/base-ui/src/scss/riseup-colors';
  @import '../../../../../../../scss/mixins';

  @keyframes shake {
    0% { transform: translate(2px, 0); }
    50% { transform: translate(-2px, 0); }
    100% { transform: translate(2px, 0); }
  }

  .current-month-cashflow-view {
    padding-bottom: 80px;

    @media (min-width: 430px) {
      padding-bottom: 20px;
    }

    .current-cashflow-view-container {
      @include responsive-padding;
      font-family: SimplerPro;
      text-align: right;
      width: 100%;
      margin: 0 auto;

      .card-wrapper {
        @include cashflow-card-margin;
      }

      .divider {
        height: 1px;
        width: 100%;
        background: $riseup_gray_0;
        margin: 25px 0;

        &.tight {
          margin-bottom: 0;
        }
      }

      .list-enter-active, .list-leave-active {
        transition: all 1s;
      }
      .list-enter, .list-leave-to {
        opacity: 0;
      }
      .list-enter-to {
        opacity: 1;
      }
    }
  }
</style>
